import { createElement } from "react";

import MapIcon from "@mui/icons-material/Map";
import { colors } from "@rubi/ui";
import { GoogleMapsProvider } from "@rubi/ui";
import ReactDOMServer from "react-dom/server";
import { ElementFactory, Question, Serializer, SvgRegistry } from "survey-core";
import { localization } from "survey-creator-core";
import { PropertyGridEditorCollection } from "survey-creator-react";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

import MapSearch from "@/components/online-reporting/custom-questions/MapWithSearch";
import { IPlace } from "@/components/online-reporting/types";

export const ADDRESS_AUTOCOMPLETE_TYPE = "address-autocomplete";

// A model for the new question type
export class QuestionAutocompleteModel extends Question {
  getType() {
    return ADDRESS_AUTOCOMPLETE_TYPE;
  }
  get autocompleteType() {
    return this.getPropertyValue("autocompleteType");
  }
  set autocompleteType(val) {
    this.setPropertyValue("autocompleteType", val);
  }

  get coverageAreaCheck() {
    return this.getPropertyValue("coverageAreaCheck");
  }
  set coverageAreaCheck(val) {
    this.setPropertyValue("coverageAreaCheck", val);
  }

  get restrictTo() {
    return this.getPropertyValue("restrictTo");
  }
  set restrictTo(val) {
    this.setPropertyValue("restrictTo", val);
  }
}

// Register `QuestionAutocompleteModel` as a model for the `address-autocomplete` type
export function registerAutocomplete() {
  if (
    !ElementFactory.Instance.getAllTypes().includes(ADDRESS_AUTOCOMPLETE_TYPE)
  ) {
    ElementFactory.Instance.registerElement(
      ADDRESS_AUTOCOMPLETE_TYPE,
      (name) => {
        return new QuestionAutocompleteModel(name);
      },
    );
  }
}

// Specify display names for the question type and its properties
// i18n: builder label deferred
const locale = localization.getLocale("");
locale.qt[ADDRESS_AUTOCOMPLETE_TYPE] = "Address Autocomplete";
locale.pe.colorPickerType = "Address autocomplete type";

// Register an SVG icon for the question type
const SURVEY_ICON_COLOR = colors.text.disabled;
const svg = ReactDOMServer.renderToString(
  <MapIcon style={{ fill: SURVEY_ICON_COLOR }} />,
);
SvgRegistry.registerIconFromSvg(ADDRESS_AUTOCOMPLETE_TYPE, svg);

// Add question type metadata for further serialization into JSON
// Guard against duplicate registration (can happen with HMR or multiple imports)
if (!Serializer.findClass(ADDRESS_AUTOCOMPLETE_TYPE)) {
  Serializer.addClass(
    ADDRESS_AUTOCOMPLETE_TYPE,
    [
      {
        name: "autocompleteType",
        default: "No Map",
        choices: ["With Map", "No Map"],
        category: "general",
        visibleIndex: 2, // After the Name and Title
      },
      {
        name: "coverageAreaCheck",
        default: false,
        choices: [false, true],
        category: "general",
        visibleIndex: 2, // After the Name and Title
      },
      {
        name: "restrictTo",
        default: null,
        choices: ["street_number", "intersection"],
        category: "general",
        visibleIndex: 3, // After the Name, Title, and Autocomplete Type
      },
    ],
    function () {
      return new QuestionAutocompleteModel("");
    },
    "question",
  );
}

// A class that renders questions of the new type in the UI
export class SurveyQuestionAddressAutocomplete extends SurveyQuestionElementBase {
  constructor(props: any) {
    super(props);
    if (!this.question.value) {
      this.question.value = { googleMapsPlace: {} };
    }

    this.state = { value: this.question.value };
  }
  get question() {
    return this.questionBase;
  }
  get value() {
    return this.question.value;
  }
  get type() {
    return this.question.autocompleteType;
  }
  get restrictTo() {
    return this.question.restrictTo;
  }

  setFieldValue = (field: string, data: IPlace) => {
    this.question.value = { ...this.question.value, [field]: data };
  };

  // Support the read-only and design modes
  get style() {
    return this.question.getPropertyValue("readOnly") ||
      this.question.isDesignMode
      ? { pointerEvents: "none" }
      : undefined;
  }

  // The address value, formatted the same way MapSearch shows it in its input
  // (prefix the place name when the formatted address doesn't already include
  // it). Empty when the question was never answered.
  get displayAddress(): string {
    if (typeof this.question.value === "string") return this.question.value;
    const place = this.question.value?.googleMapsPlace;
    if (!place?.formatted_address) return "";
    return place.formatted_address.includes(place.name || "")
      ? place.formatted_address
      : `${place.name} - ${place.formatted_address}`;
  }

  renderElement() {
    // View mode: render the selected address as read-only text, not the search
    // input. Falls back to the em-dash when unanswered, like other empty fields.
    if (this.props.isDisplayMode) {
      const address = this.displayAddress;
      const unit = this.question.value?.unit;
      const label = this.question.title || "Address";
      return (
        <div style={{ pointerEvents: "none" }}>
          <input
            className="sd-input sd-text"
            type="text"
            readOnly
            value={address}
            placeholder="—"
            aria-label={label}
          />
          {unit && (
            <div style={{ marginTop: "1rem" }}>
              <input
                className="sd-input sd-text"
                type="text"
                readOnly
                value={unit}
                aria-label={`${label} unit`}
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        <GoogleMapsProvider>
          <MapSearch
            values={this.question.value}
            setFieldValue={this.setFieldValue}
            errors={{}}
            touched={false}
            handleBlur={() => {}}
            showMap={this.type === "With Map"}
            disabled={this.props.isDisplayMode}
            restrictTo={this.restrictTo || "intersection"}
          />
        </GoogleMapsProvider>
      </div>
    );
  }
}

// Register `SurveyQuestionAddressAutocomplete` as a class that renders `address-autocomplete` questions
ReactQuestionFactory.Instance.registerQuestion(
  ADDRESS_AUTOCOMPLETE_TYPE,
  (props) => {
    return createElement(SurveyQuestionAddressAutocomplete, props);
  },
);

// Register property grid editor
PropertyGridEditorCollection.register({
  fit: function (prop: { type: string }) {
    return prop.type === "address-autocomplete";
  },
  getJSON: function () {
    return {
      type: ADDRESS_AUTOCOMPLETE_TYPE,
      autocompleteType: "No Map",
      coverageAreaCheck: false,
      restrictTo: "street_number",
    };
  },
});
